import { Download } from 'lucide-react';
import { useState } from 'react';

import { urlDeArquivo } from '@/api/http-client';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/shared/ui/tabs';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  titulo: string;
  /** Caminho da rota de export, ex.: `/v1/admin/relatorios/uso-por-usuario/exportar.csv`. */
  caminho: string;
  /** Texto do modo "Extrair completo" — deixar claro que ignora filtro e data. */
  descricaoCompleto: string;
  /** Texto do modo "Por período" — deixar claro quais filtros da tela são aplicados. */
  descricaoPeriodo: string;
  /** Filtros já ativos na tela (busca, ambiente, etc.) — só entram na querystring no modo "por período". */
  filtros?: Record<string, string | undefined>;
}

/**
 * Diálogo de exportação em CSV, compartilhado por Relatórios e pelo Funil de
 * discussões (Auditoria) — mesmo molde de `ExportarUsuariosDialogo.tsx`
 * (admin/usuarios), o primeiro a resolver este problema.
 *
 * ⚠️ Link (`<a download>`), não `fetch` — a rota devolve `text/csv` e o
 * `httpClient` faz `JSON.parse` incondicional, que quebraria no CSV (mesma
 * armadilha documentada em `BotaoDeExportar.tsx`, de análises).
 *
 * "Extrair completo" ignora TODOS os filtros da tela E qualquer recorte de
 * data — é o histórico inteiro, de propósito. "Por período" é o oposto:
 * aplica o recorte da tela (`filtros`) e ainda limita pelo intervalo de datas.
 */
export function ExportarCsvDialogo({
  aberto,
  aoFechar,
  titulo,
  caminho,
  descricaoCompleto,
  descricaoPeriodo,
  filtros,
}: Props) {
  const [modo, setModo] = useState<'completo' | 'periodo'>('completo');
  const [desde, setDesde] = useState('');
  const [ate, setAte] = useState('');

  const parametros = new URLSearchParams();
  if (modo === 'periodo') {
    for (const [chave, valor] of Object.entries(filtros ?? {})) {
      if (valor) parametros.set(chave, valor);
    }
    if (desde) parametros.set('desde', desde);
    if (ate) parametros.set('ate', ate);
  }

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{titulo}</DialogTitle>
          <DialogDescription>
            Escolha entre o histórico inteiro ou um recorte por período.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={modo} onValueChange={(v) => setModo(v as 'completo' | 'periodo')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="completo">Extrair completo</TabsTrigger>
            <TabsTrigger value="periodo">Por período</TabsTrigger>
          </TabsList>

          <TabsContent value="completo">
            <p className="text-sm text-muted-foreground">{descricaoCompleto}</p>
          </TabsContent>

          <TabsContent value="periodo" className="space-y-2">
            <p className="text-sm text-muted-foreground">{descricaoPeriodo}</p>
            <div className="flex items-center gap-2">
              <Input
                type="date"
                value={desde}
                onChange={(e) => setDesde(e.target.value)}
                aria-label="Desde"
                className="flex-1"
              />
              <span className="text-muted-foreground text-sm">até</span>
              <Input
                type="date"
                value={ate}
                onChange={(e) => setAte(e.target.value)}
                aria-label="Até"
                className="flex-1"
              />
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={aoFechar}>
            Cancelar
          </Button>
          <Button asChild>
            <a
              href={urlDeArquivo(`${caminho}?${parametros.toString()}`)}
              download
              onClick={aoFechar}
            >
              <Download className="w-4 h-4" />
              Exportar CSV
            </a>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
